import React, { useContext, useState } from 'react';
import { AudioBookmark } from '@/infrastructure/class/audio-bookmark';
import { AudioSurah } from '@/infrastructure/class/audio-surah';
import type { User } from 'firebase/auth';
import { firebaseApp } from '@/infrastructure/firebase/firebase-app';
import { FIRESTORE_COLLECTIONS } from '@/infrastructure/enum/firestore';
import { IAudioBookmark } from '@/infrastructure/interfaces/i-audio-bookmark';
import { removeElementsFromArray } from '@/utils/helpers/remove-elements-from-array';

const getApiClient = async () => {
  const { Api } = await import('@/api');
  return Api.getInstance();
};

const getFirestoreClient = async () => {
  const firestore = await import('firebase/firestore');
  return {
    db: firestore.getFirestore(firebaseApp),
    ...firestore,
  };
};

const audioBookmarkCollectionRef = (
  db: any,
  collectionFn: any,
  userUid: string
) =>
  collectionFn(
    db,
    FIRESTORE_COLLECTIONS.USERS,
    userUid,
    FIRESTORE_COLLECTIONS.AUDIOBOOKMARKS
  );
const audioBookmarkDocRef = (
  db: any,
  docFn: any,
  userUid: string,
  docId: string
) =>
  docFn(
    db,
    FIRESTORE_COLLECTIONS.USERS,
    userUid,
    FIRESTORE_COLLECTIONS.AUDIOBOOKMARKS,
    docId
  );

export interface IAudioBookmarksContext {
  audioBookmarks: IAudioBookmarksState;
  loadAudioBookmarks: (user: User) => void;
  addToAudioBookmark: (payload: IBaseAudioBookmarkUpdateEffect) => void;
  removeFromAudioBookmark: (payload: IDeleteAudioBookmarkEffect) => void;
  changeAudioBookmark: (payload: IBaseAudioBookmarkUpdateEffect) => void;
}

interface IAudioBookmarksState {
  items: AudioBookmark[];
  isBookmark: (surah: AudioSurah, timing?: number) => AudioBookmark | undefined;
}

export interface IBaseAudioBookmarkUpdateEffect {
  user: User;
  audioSurah: AudioSurah;
  timing?: number;
}

export interface IDeleteAudioBookmarkEffect {
  user: User;
  bookmark: AudioBookmark;
}

interface IAudioBookmarkProvider {
  children?: React.ReactNode;
}

const initialState = {
  items: [],
  isBookmark(surah: AudioSurah, timing?: number) {
    if (Number.isInteger(timing)) {
      return this.items.find((audioBookmark) => {
        return (
          audioBookmark.suraGuid === surah.guid &&
          audioBookmark.timing === timing
        );
      });
    }
    return this.items.find((audioBookmark) => {
      return audioBookmark.suraGuid === surah.guid;
    });
  },
} as IAudioBookmarksState;

export const AudioBookmarksContext =
  React.createContext<IAudioBookmarksContext | null>(null);

const AudioBookmarksProvider: React.FC<IAudioBookmarkProvider> = ({
  children,
}) => {
  const [audioBookmarks, setAudioBookmarks] = useState(initialState);

  const setAudioBookmarksHelper = (items: IAudioBookmark[]) => {
    setAudioBookmarks({
      ...audioBookmarks,
      items: items.map((item) => new AudioBookmark(item)),
    });
  };

  const addAudioBookmark = (bookmark: IAudioBookmark) => {
    setAudioBookmarks({
      ...audioBookmarks,
      items: [...audioBookmarks.items, new AudioBookmark(bookmark)],
    });
  };

  const removeAudioBookmark = (bookmark: AudioBookmark) => {
    setAudioBookmarks({
      ...audioBookmarks,
      items: removeElementsFromArray([...audioBookmarks.items], bookmark),
    });
  };

  const loadAudioBookmarks = async (user: User) => {
    try {
      const { db, collection, getDocs, orderBy, query } =
        await getFirestoreClient();
      const res: IAudioBookmark[] = [];
      const collectionRef = audioBookmarkCollectionRef(
        db,
        collection,
        user.uid
      );
      const q = query(collectionRef, orderBy('createdDate', 'desc'));
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        const docData = doc.data() as IAudioBookmark;
        res.push(docData);
      });
      const api = await getApiClient();
      const audioSurahs: IAudioBookmark[] = [];
      for (const bookmark of res) {
        const { data } = await api.getAudioSurahByGuid(bookmark.suraID);
        const { audioSurah } = data;
        audioSurahs.push({
          ...bookmark,
          audioSurah,
        });
      }
      setAudioBookmarksHelper(audioSurahs);
    } catch (error) {
      console.error(error);
    }
  };

  const addToAudioBookmark = async ({
    user,
    audioSurah,
    timing,
  }: IBaseAudioBookmarkUpdateEffect) => {
    try {
      const { db, addDoc, collection } = await getFirestoreClient();
      const audioBookmark: any = {
        createdDate: new Date(),
        suraID: audioSurah.guid,
      } as IAudioBookmark;
      if (timing) {
        audioBookmark.timing = timing;
      }
      const collectionRef = audioBookmarkCollectionRef(
        db,
        collection,
        user.uid
      );
      await addDoc(collectionRef, audioBookmark);
      const api = await getApiClient();
      const { data } = await api.getAudioSurahByGuid(audioSurah.guid);
      audioBookmark.audioSurah = data.audioSurah;
      addAudioBookmark(audioBookmark);
    } catch (error) {
      console.error(error);
    }
  };

  const removeFromAudioBookmark = async ({
    user,
    bookmark,
  }: IDeleteAudioBookmarkEffect) => {
    try {
      const { db, collection, deleteDoc, doc, getDocs, query, where } =
        await getFirestoreClient();
      const collectionRef = audioBookmarkCollectionRef(
        db,
        collection,
        user.uid
      );
      const q = query(collectionRef, where('suraID', '==', bookmark.suraGuid));
      const docsRef = await getDocs(q);
      let docToDelete: any = null;
      docsRef.forEach((doc) => {
        const bookmarkDoc = doc.data() as IAudioBookmark;
        if (bookmark.timing && bookmark.timing === bookmarkDoc.timing) {
          docToDelete = doc;
        } else if (!bookmark.timing && !bookmarkDoc.timing) {
          docToDelete = doc;
        }
      });
      if (docToDelete) {
        const docRef = audioBookmarkDocRef(db, doc, user.uid, docToDelete.id);
        await deleteDoc(docRef);
        removeAudioBookmark(bookmark);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const changeAudioBookmark = async ({
    user,
    audioSurah,
    timing,
  }: IBaseAudioBookmarkUpdateEffect) => {
    const bookmark = audioBookmarks.isBookmark(audioSurah, timing);
    if (bookmark) {
      await removeFromAudioBookmark({ user, bookmark });
    } else {
      await addToAudioBookmark({
        user,
        audioSurah,
        timing,
      });
    }
  };

  const value = {
    audioBookmarks,
    loadAudioBookmarks,
    addToAudioBookmark,
    removeFromAudioBookmark,
    changeAudioBookmark,
  };

  return (
    <AudioBookmarksContext.Provider value={value}>
      {children}
    </AudioBookmarksContext.Provider>
  );
};

export const useAudioBookmarks = () =>
  useContext(AudioBookmarksContext) as IAudioBookmarksContext;

export default AudioBookmarksProvider;
